// This file contains logic around special-case handling of old runs logged using Tensorboard.
// There was a W&B client bug that caused file paths to be constructed incorrectly.
// To handle this, if we detect that the run may be affected, we try fetching an alternate set of file paths.

import {
  type RunTensorboardQuery,
  type RunTensorboardQueryVariables,
} from '../../../generated/graphql';
import {RUN_TENSORBOARD_QUERY} from '../../../graphql/runTensorboardQuery';
import {type RunSignature} from '../../../types/run';
import {type TileMediaSpec} from '../../MediaCard';
import {fetchGQL} from './util';

export async function shouldTryTensorboardSpecialHandling({
  runSignature,
  mediaType,
  mediaIndex,
}: TileMediaSpec): Promise<boolean> {
  return (
    mediaType === 'image' &&
    mediaIndex != null &&
    (await isRunUsingTensorboard(runSignature))
  );
}

async function isRunUsingTensorboard(
  runSignature: RunSignature
): Promise<boolean> {
  const data = await fetchGQL<
    RunTensorboardQueryVariables,
    RunTensorboardQuery
  >({
    query: RUN_TENSORBOARD_QUERY,
    variables: runSignature,
  });
  const usingTensorboard = data?.project?.run?.servicesAvailable?.tensorboard;
  return usingTensorboard ?? false;
}

// Media file paths are constructed in a detereministic way:
// ${mediaKey}_${mediaStep}_${mediaIndex}_${filename}
// There was a bug that reported `mediaStep` as 0, 1, 2, etc.
// instead of the actual steps for which they're logged.
// This function replaces a path's `mediaStep` to what it would be
// in the "bugged" state, which is how affected paths are stored in the backend.
export function getZeroIndexFilePath(filePath: string, index: number): string {
  const originalInfo = filePath.split('_');
  const suffix = originalInfo[originalInfo.length - 1];
  const prefix = originalInfo.slice(0, originalInfo.length - 2).join('_');
  const zeroIndexFilePath = `${prefix}_${index}_${suffix}`;
  return zeroIndexFilePath;
}
